/**
 * The marketplace's Weapon Finishes category — E5's flat 900-gem finish, one per weapon
 * (`shared/src/weapon-skins.ts`: `BASE_WEAPON_SKINS`).
 *
 * Purchase goes through `POST /game/arsenal/skins/purchase` — the same route the 15
 * stride-ladder finishes already buy through, so a finish bought here and one bought from the
 * Armoury/Inventory screens are indistinguishable to the server. There is no equip button: a
 * finish is worn per loadout slot, not globally, so the owned state points at the Armoury the
 * same way an owned character skin already does in the Operators grid.
 */
import { confirmAction } from '../ui';
import { formatNumber, t } from '../../i18n';
import {
  fetchWeaponSkinsCatalogue,
  isBaseWeaponSkinId,
  purchaseWeaponSkin,
  type WeaponSkinEntry,
  type WeaponSkinsCatalogueView,
} from '../weapon-skins-api';
import { tintSquare, type MarketItem } from './marketplace-tile';
import { createMarketSection, type MarketSection } from './marketplace-section';

function toItem(
  entry: WeaponSkinEntry,
  progress: WeaponSkinsCatalogueView['progress'],
  act: (mutate: () => Promise<unknown>) => void,
  busy: boolean,
): MarketItem {
  const affordable =
    entry.currency === 'PREMIUM' ? progress.premiumBalance >= entry.priceCoins : progress.softBalance >= entry.priceCoins;

  return {
    key: `weaponSkin-${entry.id}`,
    category: 'weaponSkins',
    name: entry.name,
    description: t('shop.weaponSkins.note'),
    art: tintSquare(entry.tint),
    price: { amount: entry.priceCoins, currency: entry.currency },
    owned: entry.owned,
    equipped: false,
    affordable,
    hint: entry.owned ? t('shop.equipInArmoury') : undefined,
    action: entry.owned
      ? undefined
      : {
          label: affordable ? t('common.buy') : entry.currency === 'PREMIUM' ? t('shop.needGems') : t('shop.needCredits'),
          variant: affordable ? 'primary' : 'ghost',
          disabled: busy || !affordable,
          onClick: () =>
            void (async () => {
              const confirmed = await confirmAction({
                title: t('confirmDialog.buyTitle'),
                body: t('confirmDialog.buyBody', {
                  item: entry.name,
                  price: `${formatNumber(entry.priceCoins)} ${entry.currency === 'PREMIUM' ? t('common.gems') : t('common.credits')}`,
                }),
              });
              if (!confirmed) return;
              act(() => purchaseWeaponSkin(entry.id));
            })(),
        },
  };
}

export function createWeaponSkinsSection(): MarketSection {
  return createMarketSection<WeaponSkinsCatalogueView>({
    id: 'weaponSkins',
    label: t('shop.weaponSkins.title'),
    note: t('shop.weaponSkins.note'),
    fetchCatalogue: fetchWeaponSkinsCatalogue,
    toItems: (catalogue, act, busy) =>
      catalogue.weaponSkins.filter((entry) => isBaseWeaponSkinId(entry.id)).map((entry) => toItem(entry, catalogue.progress, act, busy)),
  });
}
